import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { ArrowLeft, Sparkles, CheckCircle2, Loader2, RefreshCw, AlertCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';

const ContentStrategy = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingProfile, setCheckingProfile] = useState(true); // Add loading state for profile check
  const [topics, setTopics] = useState([]);
  const [topicsDetailed, setTopicsDetailed] = useState([]);
  const [dataSource, setDataSource] = useState(null);
  const [profileComplete, setProfileComplete] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [topicCounts, setTopicCounts] = useState({}); // Track how many blogs per topic
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [generationComplete, setGenerationComplete] = useState(null);
  const [usage, setUsage] = useState(null);
  const [jobStatus, setJobStatus] = useState(null); // Track active job status
  const [retrying, setRetrying] = useState(false);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    checkProfile();
    fetchUsage();
    
    // Cleanup polling on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [user, navigate]);

  // Poll job status when there's an active job
  useEffect(() => {
    if (generationComplete?.job_id && !jobStatus?.status?.match(/completed|failed|retried/)) {
      pollJobStatus(generationComplete.job_id);
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [generationComplete?.job_id]);

  const pollJobStatus = (jobId) => {
    // Clear any existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    
    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/generate/job/${jobId}`);
        setJobStatus(response.data);
        
        // Stop polling if job is done
        if (response.data.status === 'completed' || response.data.status === 'failed' || response.data.status === 'retried') {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          
          // Refresh usage after completion
          fetchUsage();
        }
      } catch (error) {
        console.error('Error polling job status:', error);
      }
    };
    
    // Check immediately, then every 5 seconds
    checkStatus();
    pollIntervalRef.current = setInterval(checkStatus, 5000);
  };

  const handleRetryJob = async () => {
    if (!generationComplete?.job_id) return;
    
    setRetrying(true);
    try {
      const response = await axios.post(`${API}/generate/retry-job/${generationComplete.job_id}`);
      
      if (response.data.success) {
        // Update to track new job
        setGenerationComplete({
          job_id: response.data.new_job_id,
          total: jobStatus?.total_count || 1,
          message: response.data.message
        });
        setJobStatus(null);
      }
    } catch (error) {
      console.error('Error retrying job:', error);
      alert(error.response?.data?.detail || 'Failed to retry. Please try again.');
    } finally {
      setRetrying(false);
    }
  };

  const fetchUsage = async () => {
    try {
      const response = await axios.get(`${API}/usage/${user.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setUsage(response.data);
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  };

  const checkProfile = async () => {
    setCheckingProfile(true);
    try {
      const response = await axios.get(`${API}/profile/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data) {
        const profile = response.data;
        const isComplete = profile.business_name && 
                          profile.industry && 
                          profile.services_list && 
                          profile.services_list.length > 0 &&
                          profile.service_areas &&
                          profile.service_areas.length > 0;
        
        setProfileComplete(isComplete);
        setProfileData(profile);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setCheckingProfile(false);
    }
  };

  const handleGenerateStrategy = async () => {
    setLoading(true);
    setTopics([]);
    setSelectedTopics([]);
    setGenerationComplete(null);

    try {
      const response = await axios.post(`${API}/strategy/generate?user_id=${user.id}`, {}, {
        timeout: 120000 // 2 minute timeout for strategy generation
      });
      setTopics(response.data.topics || []);
      setTopicsDetailed(response.data.topics_detailed || []);
      setDataSource(response.data.data_source || null);
    } catch (error) {
      console.error('Error generating strategy:', error);
      if (error.code === 'ECONNABORTED') {
        alert('Request timed out. The server is taking too long. Please try again.');
      } else {
        alert(error.response?.data?.detail || 'Error generating content strategy. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleTopic = (index) => {
    if (selectedTopics.includes(index)) {
      setSelectedTopics(selectedTopics.filter(i => i !== index));
    } else {
      setSelectedTopics([...selectedTopics, index]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedTopics.length === topics.length) {
      setSelectedTopics([]);
    } else {
      setSelectedTopics(topics.map((_, i) => i));
    }
  };

  // Calculate total blogs to generate (including duplicates)
  const getTotalBlogCount = () => {
    return selectedTopics.reduce((total, index) => {
      return total + (topicCounts[index] || 1);
    }, 0);
  };

  const handleBulkGenerate = async () => {
    if (selectedTopics.length === 0) {
      alert('Please select at least one topic to generate');
      return;
    }

    // Check usage limits with new API structure
    if (usage && !usage.can_generate_blog) {
      if (usage.plan === 'free') {
        alert(`You've used all ${usage.blogs_limit} free blogs. Upgrade to Starter ($27/mo) to continue!`);
      } else {
        alert(`You've reached your daily limit. Come back tomorrow or upgrade for more!`);
      }
      return;
    }

    setBulkGenerating(true);
    setGenerationComplete(null);

    try {
      // Build questions array with duplicates based on topicCounts
      const questionsToGenerate = [];
      selectedTopics.forEach(index => {
        const topic = topics[index];
        const count = topicCounts[index] || 1;
        for (let i = 0; i < count; i++) {
          questionsToGenerate.push(topic);
        }
      });
      
      // Start background job - returns immediately
      const response = await axios.post(`${API}/generate/bulk`, {
        user_id: user.id,
        questions: questionsToGenerate
      });

      if (response.data.job_id) {
        // Job started successfully - show message and allow navigation
        setGenerationComplete({
          job_id: response.data.job_id,
          total: questionsToGenerate.length,
          message: `Started generating ${questionsToGenerate.length} answer post(s) in background. You can navigate away - posts will appear in your Content Library as they complete.`
        });
        
        // Clear selections
        setSelectedTopics([]);
        setTopicCounts({});
        
        // Refresh usage
        await fetchUsage();
      }

    } catch (error) {
      console.error('Error generating blogs:', error);
      let errorMsg = 'Error generating blogs. Please try again.';
      if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      }
      alert(errorMsg);
    } finally {
      setBulkGenerating(false);
    }
  };

  if (!user) return null;

  // Show loading spinner while checking profile
  if (checkingProfile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Helmet>
          <title>Content Strategy | QuillFire - Discover What Customers Search For</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!profileComplete) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Helmet>
          <title>Content Strategy | QuillFire - Discover What Customers Search For</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </nav>

        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <div className="bg-white rounded-2xl p-12 border border-gray-200 shadow-sm">
            <img src="/quillfire-logo.png" alt="QuillFire" className="w-16 h-16 mx-auto mb-6 rounded-lg" />
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Complete Your Content Strategy Setup</h1>
            <p className="text-gray-600 mb-8">
              To generate strategic content, please complete your profile with:
              <ul className="text-left max-w-md mx-auto mt-4 space-y-2">
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Your services/programs</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Service areas (cities/neighborhoods)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Target audiences (optional but recommended)</span>
                </li>
              </ul>
            </p>
            <Button 
              onClick={() => navigate('/profile')}
              className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 text-lg"
            >
              Complete Profile Setup
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center space-x-2">
            <img src="/quillfire-logo.png" alt="QuillFire" className="w-8 h-8 rounded-lg" />
            <span className="text-xl font-bold text-gray-900">Content Strategy</span>
          </div>
          <div className="w-20" />
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        
        {/* Profile Summary */}
        {profileData && (
          <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl p-6 mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">{profileData.business_name}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-600 font-medium">Services ({profileData.services_list?.length || 0})</p>
                <p className="text-gray-800">{profileData.services_list?.slice(0, 3).join(', ')}{profileData.services_list?.length > 3 ? '...' : ''}</p>
              </div>
              <div>
                <p className="text-gray-600 font-medium">Service Areas ({profileData.service_areas?.length || 0})</p>
                <p className="text-gray-800">{profileData.service_areas?.slice(0, 3).join(', ')}{profileData.service_areas?.length > 3 ? '...' : ''}</p>
              </div>
              <div>
                <p className="text-gray-600 font-medium">Target Audiences ({profileData.target_audiences_list?.length || 0})</p>
                <p className="text-gray-800">{profileData.target_audiences_list?.slice(0, 2).join(', ')}{profileData.target_audiences_list?.length > 2 ? '...' : '' || 'Not specified'}</p>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/profile')}
              variant="outline"
              size="sm"
              className="mt-4"
            >
              Edit Profile
            </Button>
          </div>
        )}

        {/* Generate Button */}
        {topics.length === 0 && (
          <div className="text-center py-16">
            <img src="/quillfire-logo.png" alt="QuillFire" className="w-20 h-20 mx-auto mb-6 rounded-lg" />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Generate Your Content Strategy</h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Our AI will create 25-30 strategic question topics based on your services, locations, and target audiences.
              Get a perfect mix of location-based, audience-focused, educational, and seasonal content.
            </p>
            <Button 
              onClick={handleGenerateStrategy}
              disabled={loading}
              className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 text-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generating Strategy...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generate Content Strategy
                </>
              )}
            </Button>
          </div>
        )}

        {/* Topics List */}
        {topics.length > 0 && (
          <div>
            {/* Usage Indicator - Updated for new pricing model */}
            {usage && (
              <div className={`rounded-xl border p-4 mb-6 ${
                !usage.can_generate_blog 
                  ? 'bg-red-50 border-red-200' 
                  : (usage.daily_blogs_remaining || usage.blogs_remaining || 0) <= 1 
                    ? 'bg-amber-50 border-amber-200' 
                    : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {!usage.can_generate_blog ? (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    ) : (
                      <Sparkles className="w-5 h-5 text-blue-600" />
                    )}
                    <div>
                      <p className={`font-semibold ${!usage.can_generate_blog ? 'text-red-800' : 'text-gray-900'}`}>
                        {!usage.can_generate_blog 
                          ? (usage.plan === 'free' ? 'Free trial limit reached' : 'Daily limit reached')
                          : `${usage.plan.charAt(0).toUpperCase() + usage.plan.slice(1)} Plan: ${usage.blogs_message || 'Ready to generate'}`
                        }
                      </p>
                      <p className="text-sm text-gray-600">
                        {usage.can_generate_blog 
                          ? `${usage.has_images ? '✓ AI Images' : ''}${usage.has_autopost ? ' • ✓ Auto-posting' : ''}${usage.searches_remaining !== undefined ? ` • ${usage.searches_remaining} searches left` : ''}`
                          : (usage.plan === 'free' 
                              ? 'Your free trial has ended. Upgrade to continue creating content!' 
                              : 'Your daily limit resets tomorrow. Upgrade for more!')
                        }
                      </p>
                    </div>
                  </div>
                  {(usage.plan === 'free' || usage.plan === 'starter') && (
                    <Button
                      onClick={() => window.location.href = '/#pricing'}
                      variant="outline"
                      size="sm"
                      className="border-orange-600 text-orange-600 hover:bg-orange-50"
                    >
                      Upgrade
                    </Button>
                  )}
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Your Strategic Content Topics ({topics.length})
                  </h3>
                  {dataSource && (
                    <p className="text-sm text-green-600 flex items-center gap-1 mt-1">
                      <CheckCircle2 className="w-4 h-4" />
                      Based on verified Google search data
                    </p>
                  )}
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={handleGenerateStrategy}
                    variant="outline"
                    size="sm"
                    disabled={loading}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Regenerate
                  </Button>
                  <Button
                    onClick={toggleSelectAll}
                    variant="outline"
                    size="sm"
                  >
                    {selectedTopics.length === topics.length ? 'Deselect All' : 'Select All'}
                  </Button>
                  <Button
                    onClick={handleBulkGenerate}
                    disabled={selectedTopics.length === 0 || bulkGenerating}
                    className={`${bulkGenerating ? 'bg-orange-400' : 'bg-orange-600 hover:bg-orange-700'} text-white min-w-[200px]`}
                  >
                    {bulkGenerating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating... (1-2 min per post)
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate {getTotalBlogCount()} Answer Post{getTotalBlogCount() !== 1 ? 's' : ''}
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {bulkGenerating && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-5 h-5 animate-spin text-orange-600" />
                    <div>
                      <p className="text-orange-800 font-medium">
                        Generating {getTotalBlogCount()} answer post{getTotalBlogCount() !== 1 ? 's' : ''}...
                      </p>
                      <p className="text-orange-600 text-sm">
                        This takes 1-2 minutes per post. Please don't close this page.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {generationComplete && (
                <div className={`border rounded-lg p-4 mb-4 ${
                  jobStatus?.status === 'failed' ? 'bg-red-50 border-red-200' :
                  jobStatus?.status === 'completed' ? 'bg-green-50 border-green-200' :
                  'bg-blue-50 border-blue-200'
                }`}>
                  {/* Progress indicator */}
                  {jobStatus && jobStatus.status === 'processing' && (
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-blue-700">Generating posts...</span>
                        <span className="text-sm text-blue-600">
                          {jobStatus.completed_count || 0} / {jobStatus.total_count || generationComplete.total} completed
                        </span>
                      </div>
                      <div className="w-full bg-blue-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                          style={{ 
                            width: `${((jobStatus.completed_count || 0) / (jobStatus.total_count || generationComplete.total)) * 100}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                  )}
                  
                  {/* Status message */}
                  <p className={`font-medium flex items-center gap-2 ${
                    jobStatus?.status === 'failed' ? 'text-red-800' :
                    jobStatus?.status === 'completed' ? 'text-green-800' :
                    'text-blue-800'
                  }`}>
                    {jobStatus?.status === 'processing' && <Loader2 className="w-4 h-4 animate-spin" />}
                    {jobStatus?.status === 'completed' && <CheckCircle2 className="w-4 h-4" />}
                    {jobStatus?.status === 'failed' && <XCircle className="w-4 h-4" />}
                    
                    {jobStatus?.status === 'completed' 
                      ? `✅ Generated ${jobStatus.completed_count} post(s)${jobStatus.failed_count > 0 ? ` (${jobStatus.failed_count} failed)` : ''}!`
                      : jobStatus?.status === 'failed'
                      ? `❌ Generation failed${jobStatus.completed_count > 0 ? ` (${jobStatus.completed_count} succeeded)` : ''}`
                      : `🚀 ${generationComplete.message}`
                    }
                  </p>
                  
                  {/* Error message if failed */}
                  {jobStatus?.status === 'failed' && jobStatus.error_message && (
                    <p className="text-sm text-red-600 mt-2 bg-red-100 p-2 rounded">
                      {jobStatus.error_message}
                    </p>
                  )}
                  
                  {/* Action buttons */}
                  <div className="mt-3 flex gap-3 flex-wrap">
                    <button 
                      onClick={() => navigate('/content-library')}
                      className={`underline text-sm ${
                        jobStatus?.status === 'failed' ? 'text-red-700' :
                        jobStatus?.status === 'completed' ? 'text-green-700' :
                        'text-blue-700'
                      }`}
                    >
                      Go to Content Library →
                    </button>
                    
                    {/* Retry button for failed jobs */}
                    {jobStatus?.status === 'failed' && (
                      <button 
                        onClick={handleRetryJob}
                        disabled={retrying}
                        className="flex items-center gap-1 text-sm text-orange-700 hover:text-orange-800 underline"
                      >
                        {retrying ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Retrying...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-3 h-3" />
                            Retry Failed Posts
                          </>
                        )}
                      </button>
                    )}
                    
                    {/* Dismiss button */}
                    {(jobStatus?.status === 'completed' || jobStatus?.status === 'failed') && (
                      <button 
                        onClick={() => {
                          setGenerationComplete(null);
                          setJobStatus(null);
                        }}
                        className="text-sm text-gray-500 hover:text-gray-700"
                      >
                        Dismiss
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {topics.map((topic, index) => {
                  const detailed = topicsDetailed[index] || {};
                  const keywordType = detailed.keyword_type || 'general';
                  const isSelected = selectedTopics.includes(index);
                  const count = topicCounts[index] || 1;
                  
                  return (
                    <div
                      key={index}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        isSelected
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 bg-white hover:border-orange-300'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleTopic(index)}
                          className="mt-1 w-4 h-4 text-orange-600 rounded focus:ring-orange-500 cursor-pointer"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p 
                              className="text-gray-900 cursor-pointer flex-1"
                              onClick={() => toggleTopic(index)}
                            >
                              {topic}
                            </p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ml-2 whitespace-nowrap ${
                              keywordType === 'question' ? 'bg-blue-100 text-blue-700' :
                              keywordType === 'comparison' ? 'bg-purple-100 text-purple-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {keywordType === 'question' ? '❓ Question' :
                               keywordType === 'comparison' ? '⚖️ Comparison' :
                               '📝 Topic'}
                            </span>
                          </div>
                          {isSelected && (
                            <div className="mt-2 flex items-center gap-2">
                              <span className="text-sm text-gray-600">Generate</span>
                              <select
                                value={count}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setTopicCounts({
                                    ...topicCounts,
                                    [index]: parseInt(e.target.value)
                                  });
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-orange-500 focus:border-orange-500"
                              >
                                <option value={1}>1 answer post</option>
                                <option value={2}>2 answer posts</option>
                                <option value={3}>3 answer posts</option>
                                <option value={4}>4 answer posts</option>
                                <option value={5}>5 answer posts</option>
                              </select>
                              <span className="text-xs text-gray-500">(more posts = better ranking potential)</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentStrategy;
